import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import {
  CheckCircleOutline as ResolveIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { dt } from "../../../../shared/themes/redesignTokens";
import { EnrichedServiceReport } from "../../store/serviceReportsSlice";
import serviceReportsApi from "../../api/serviceReportsApi";

// ─── Props ──────────────────────────────────────────────────

interface BulkResolveDialogProps {
  open: boolean;
  items: EnrichedServiceReport[];
  onClose: () => void;
  onResolved: (count: number) => void;
  translate: (key: string) => string;
}

// ─── Component ──────────────────────────────────────────────

const BulkResolveDialog: React.FC<BulkResolveDialogProps> = ({
  open,
  items,
  onClose,
  onResolved,
  translate,
}) => {
  const [solvingDescription, setSolvingDescription] = useState("");
  const [costValue, setCostValue] = useState("");
  const [closeDate, setCloseDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSolvingDescription("");
      setCostValue("");
      setCloseDate(dayjs().format("YYYY-MM-DD"));
      setError("");
      setSubmitting(false);
      setProgress(0);
    }
  }, [open]);

  // Inject keyframes
  useEffect(() => {
    const id = "bulk-resolve-dialog-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes bulkResolveSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const canSubmit = solvingDescription.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");

    const payload = {
      solvingDescription: solvingDescription.trim(),
      costValue: costValue ? parseFloat(costValue) : undefined,
      closeDate: dayjs(closeDate).toISOString(),
    };

    let resolved = 0;
    for (let i = 0; i < items.length; i++) {
      const sr = items[i].sr;
      if (!sr.number) continue;
      setProgress(i + 1);
      try {
        await serviceReportsApi.resolveSr(sr.number, payload);
        resolved++;
      } catch {
        // Continue with others even if one fails
      }
    }

    if (resolved === 0) {
      setError(translate("serviceReports.bulk.resolveFailedAll"));
      setSubmitting(false);
    } else {
      onResolved(resolved);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={submitting ? undefined : onClose}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.25)",
          animation: "resolveSrBackdropIn 0.2s ease",
        }}
      />

      {/* Dialog */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "440px",
          mx: "16px",
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
          animation: "bulkResolveSlideUp 0.3s ease",
        }}
      >
        {/* Terracotta accent bar */}
        <Box
          sx={{
            height: "4px",
            background: "linear-gradient(90deg, #D4572B 0%, #E8A817 100%)",
          }}
        />

        {/* Content */}
        <Box sx={{ padding: "28px 28px 24px" }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "14px", mb: "20px" }}>
            <Box
              sx={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                backgroundColor: "rgba(212,87,43,0.10)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ResolveIcon sx={{ fontSize: "20px", color: "#D4572B" }} />
            </Box>
            <Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#0C1117",
                  lineHeight: 1.3,
                }}
              >
                {translate("serviceReports.bulk.resolveTitle")}
              </Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  color: "#6B7280",
                  mt: "4px",
                }}
              >
                {translate("serviceReports.bulk.resolveSubtitle").replace("{{count}}", String(items.length))}
              </Box>
            </Box>
          </Box>

          {/* Solving Description */}
          <Box sx={{ mb: "16px" }}>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#0C1117",
                mb: "6px",
              }}
            >
              {translate("serviceReports.actions.solvingDescription")} *
            </Box>
            <textarea
              value={solvingDescription}
              onChange={(e) => setSolvingDescription(e.target.value)}
              placeholder={translate("serviceReports.actions.solvingDescriptionPlaceholder")}
              rows={4}
              disabled={submitting}
              style={{
                width: "100%",
                boxSizing: "border-box",
                fontFamily: "'Sora', sans-serif",
                fontSize: "13px",
                lineHeight: 1.6,
                color: "#0C1117",
                backgroundColor: "#F7F5F0",
                border: "1.5px solid transparent",
                borderRadius: "10px",
                padding: "12px 14px",
                resize: "vertical",
                outline: "none",
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                opacity: submitting ? 0.6 : 1,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#D4572B";
                e.target.style.boxShadow = "0 0 0 3px rgba(212,87,43,0.10)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "transparent";
                e.target.style.boxShadow = "none";
              }}
            />
          </Box>

          {/* Cost + Close Date row */}
          <Box sx={{ display: "flex", gap: "14px", mb: error ? "12px" : "24px" }}>
            {/* Cost */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#0C1117",
                  mb: "6px",
                }}
              >
                {translate("serviceReports.actions.cost")}
              </Box>
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontFamily: dt.fontMono,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#8C9099",
                    pointerEvents: "none",
                  }}
                >
                  EUR
                </Box>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={costValue}
                  onChange={(e) => setCostValue(e.target.value)}
                  placeholder="0.00"
                  disabled={submitting}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "13px",
                    color: "#0C1117",
                    backgroundColor: "#F7F5F0",
                    border: "1.5px solid transparent",
                    borderRadius: "10px",
                    padding: "10px 14px 10px 48px",
                    outline: "none",
                    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                    opacity: submitting ? 0.6 : 1,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#D4572B";
                    e.target.style.boxShadow = "0 0 0 3px rgba(212,87,43,0.10)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "transparent";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  color: "#D4572B",
                  mt: "4px",
                }}
              >
                {translate("serviceReports.actions.optional")}
              </Box>
            </Box>

            {/* Close Date */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#0C1117",
                  mb: "6px",
                }}
              >
                {translate("serviceReports.actions.closeDate")}
              </Box>
              <input
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                disabled={submitting}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "13px",
                  color: "#D4572B",
                  backgroundColor: "#F7F5F0",
                  border: "1.5px solid transparent",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  outline: "none",
                  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                  opacity: submitting ? 0.6 : 1,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#D4572B";
                  e.target.style.boxShadow = "0 0 0 3px rgba(212,87,43,0.10)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "transparent";
                  e.target.style.boxShadow = "none";
                }}
              />
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  color: "#8C9099",
                  mt: "4px",
                }}
              >
                {translate("serviceReports.actions.defaultsToday")}
              </Box>
            </Box>
          </Box>

          {/* Error message */}
          {error && (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                color: "#DC2626",
                backgroundColor: "rgba(220,38,38,0.06)",
                borderRadius: "8px",
                padding: "8px 12px",
                mb: "16px",
              }}
            >
              {error}
            </Box>
          )}

          {/* Progress indicator */}
          {submitting && (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#D4572B",
                textAlign: "center",
                mb: "16px",
              }}
            >
              {translate("serviceReports.bulk.resolvingProgress")
                .replace("{{current}}", String(progress))
                .replace("{{total}}", String(items.length))}
            </Box>
          )}

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px" }}>
            <Box
              onClick={submitting ? undefined : onClose}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#8C9099",
                cursor: submitting ? "default" : "pointer",
                padding: "8px 16px",
                borderRadius: "8px",
                transition: "color 0.15s ease",
                opacity: submitting ? 0.5 : 1,
                "&:hover": submitting ? {} : { color: "#4A4F57" },
              }}
            >
              {translate("serviceReports.actions.cancel")}
            </Box>
            <Box
              onClick={handleSubmit}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#FFFFFF",
                background: canSubmit
                  ? "linear-gradient(135deg, #D4572B, #B8451F)"
                  : "#D1CCC4",
                borderRadius: "24px",
                padding: "10px 28px",
                cursor: canSubmit ? "pointer" : "default",
                transition: "all 0.2s ease",
                boxShadow: canSubmit ? "0 2px 8px rgba(212,87,43,0.25)" : "none",
                "&:hover": canSubmit
                  ? {
                      background: "linear-gradient(135deg, #B8451F, #D4572B)",
                      boxShadow: "0 4px 12px rgba(212,87,43,0.30)",
                    }
                  : {},
              }}
            >
              {submitting
                ? translate("serviceReports.actions.resolving")
                : translate("serviceReports.bulk.resolveAllButton")}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkResolveDialog;
